import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Plus, Edit2, Trash2, LogOut, User, Settings } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

function WorkspacesPage() {
  const { user, subscription, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [workspaceName, setWorkspaceName] = useState("");
  const [saving, setSaving] = useState(false);

  const canUseWorkspaces = subscription?.effective_plan && 
    (subscription.effective_plan === "pro" || subscription.effective_plan === "creator" || subscription.in_trial);

  useEffect(() => {
    if (!canUseWorkspaces) {
      navigate("/pricing");
      return;
    }
    fetchWorkspaces();
  }, [canUseWorkspaces]);

  const fetchWorkspaces = async () => {
    try {
      const response = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(response.data.workspaces || []);
    } catch (error) {
      console.error("Error fetching workspaces:", error);
      if (error.response?.status === 403) {
        navigate("/pricing");
      } else {
        toast.error("Failed to load workspaces");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      toast.error("Please enter a workspace name");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/workspaces`,
        { name: workspaceName },
        { withCredentials: true }
      );
      toast.success("Workspace created!");
      setShowCreateModal(false);
      setWorkspaceName("");
      fetchWorkspaces();
    } catch (error) {
      console.error("Error creating workspace:", error);
      toast.error(error.response?.data?.detail || "Failed to create workspace");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWorkspace = async () => {
    if (!workspaceName.trim()) {
      toast.error("Please enter a workspace name");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/workspaces/${editingWorkspace.id}`,
        { name: workspaceName },
        { withCredentials: true }
      );
      toast.success("Workspace updated!");
      setEditingWorkspace(null);
      setWorkspaceName("");
      fetchWorkspaces();
    } catch (error) {
      console.error("Error updating workspace:", error);
      toast.error(error.response?.data?.detail || "Failed to update workspace");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    if (!window.confirm("Are you sure? All members will lose access to shared videos.")) {
      return;
    }

    try {
      await axios.delete(`${API}/workspaces/${workspaceId}`, { withCredentials: true });
      toast.success("Workspace deleted");
      fetchWorkspaces();
    } catch (error) {
      console.error("Error deleting workspace:", error);
      toast.error(error.response?.data?.detail || "Failed to delete workspace");
    }
  };

  const openEditModal = (workspace) => {
    setEditingWorkspace(workspace);
    setWorkspaceName(workspace.name);
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <Button onClick={() => navigate("/dashboard")} variant="ghost">
              Dashboard
            </Button>
            <Button onClick={() => navigate("/library")} variant="ghost">
              My Videos
            </Button>
            <Button onClick={() => navigate("/projects")} variant="ghost">
              Projects
            </Button>
            <Button onClick={() => navigate("/workspaces")} variant="ghost" className="active">
              Workspaces
            </Button>
            <div className="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Team Workspaces</h1>
            <p className="page-subtitle">Collaborate and share video summaries with your team</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="create-button">
            <Plus size={20} /> New Workspace
          </Button>
        </div>

        {workspaces.length === 0 ? (
          <Card className="empty-state-card">
            <CardContent className="empty-state">
              <Users size={64} className="empty-icon" />
              <h3>No workspaces yet</h3>
              <p>Create a workspace to start sharing summaries with your team</p>
              <Button onClick={() => setShowCreateModal(true)} className="create-button">
                <Plus size={20} /> Create Workspace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="workspaces-grid">
            {workspaces.map((workspace) => (
              <Card key={workspace.id} className="workspace-card">
                <CardHeader>
                  <div className="workspace-header">
                    <div className="workspace-info">
                      <CardTitle className="workspace-name">{workspace.name}</CardTitle>
                      {workspace.role === "owner" && (
                        <span className="owner-badge">Owner</span>
                      )}
                    </div>
                    {workspace.role === "owner" && (
                      <div className="workspace-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(workspace)}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteWorkspace(workspace.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="workspace-stats">
                    <Users size={20} />
                    <span>{workspace.member_count || 1} members</span>
                  </div>
                  <Button
                    variant="outline"
                    className="manage-button"
                    onClick={() => navigate(`/workspaces/${workspace.id}`)}
                  >
                    <Settings size={16} /> Manage Workspace
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingWorkspace) && (
        <div className="modal-overlay">
          <div className="modal-content workspace-modal">
            <div className="modal-header">
              <h2>{editingWorkspace ? "Edit Workspace" : "New Workspace"}</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingWorkspace(null);
                  setWorkspaceName("");
                }}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Workspace Name</label>
                <Input
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="e.g., Marketing Team"
                  maxLength={50}
                />
              </div>
              <Button
                onClick={editingWorkspace ? handleUpdateWorkspace : handleCreateWorkspace}
                disabled={saving}
                className="save-button"
              >
                {saving ? "Saving..." : editingWorkspace ? "Update Workspace" : "Create Workspace"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkspacesPage;
